<script setup lang="ts">
import { ref, onBeforeMount, watch, computed } from "vue";
import { useApiClient } from "@/api/client";
import useDialog from "@/ui/composables/useDialog";
import { toastApiResult } from "@/toaster";
import BexLabelDO = Modules.BookingExperts.Data.BexLabelDO;
import AddEditLabel from "@/modules/pro_loyalty/components/AddEditLabel.vue";

const getEmptyLabel = (): BexLabelDO => {
	return {
		uuid: null,
		name: "",
		background: "",
	};
};

const currentPage = ref<number>(1);
const itemsPerPage = ref<number>(100);
const totalItems = ref<number>(1);
const bexLabels = ref<BexLabelDO[]>();
const isLoading = ref<boolean>(true);

const isLabelDialogVisible = ref<boolean>(false);
const bexLabelToEdit = ref<BexLabelDO>();

const getBexLabels = async () => {
	isLoading.value = true;
	const response = await useApiClient("proLoyalty").getLabels({
		page: currentPage.value,
		per_page: itemsPerPage.value,
		sortBy: [],
	});
	if (response.isOk()) {
		bexLabels.value = response.value.data;
		totalItems.value = response.value.total;
	}
	isLoading.value = false;
};

const addNewLabel = () => {
	bexLabelToEdit.value = getEmptyLabel();
	isLabelDialogVisible.value = true;
};

const editLabel = (bexLabel: BexLabelDO) => {
	bexLabelToEdit.value = bexLabel;
	isLabelDialogVisible.value = true;
};

const noOfPages = computed(() =>
	Math.ceil(totalItems.value / itemsPerPage.value)
);

const removeLabel = async (bexLabel: BexLabelDO) => {
	const confirm = await useDialog().confirm({
		title: "Delete Label",
		message: `Are you sure you want to delete the Label?`,
		isDelete: true,
	});

	if (confirm) {
		const response = await toastApiResult(
			useApiClient("proLoyalty").removeLabel(bexLabel)
		);
		if (response.isOk()) {
			getBexLabels();
		}
	}
};

watch(currentPage, () => getBexLabels());
onBeforeMount(() => getBexLabels());
</script>

<template>
	<add-edit-label
		:bex-label="bexLabelToEdit"
		v-model="isLabelDialogVisible"
		v-if="isLabelDialogVisible"
		@refresh-labels="getBexLabels"
	/>
	<v-card class="px-5 mt-10">
		<div class="d-flex my-5">
			<v-spacer />
			<v-btn color="primary" variant="flat" @click="addNewLabel">
				Add new Label
			</v-btn>
		</div>
		<div v-if="!isLoading">
			<v-chip
				v-for="bexLabel in bexLabels"
				:color="bexLabel.background"
				class="pr-0 mr-5 mt-5"
				:style="`height: 40px;`"
				label
			>
				<div style="min-width: 150px" class="d-flex">
					<div class="bold">{{ bexLabel.name }}</div>
				</div>
				<template v-slot:append="">
					<div class="d-flex">
						<v-btn
							icon="mdi-pencil-outline"
							rounded="0"
							class="ma-0"
							size="40"
							variant="tonal"
							@click="editLabel(bexLabel)"
							:color="bexLabel.background"
						>
						</v-btn>
						<v-btn
							icon="mdi-trash-can-outline"
							rounded="0"
							class="ma-0"
							size="40"
							variant="tonal"
							@click="removeLabel(bexLabel)"
							:color="bexLabel.background"
						>
						</v-btn>
					</div>
				</template>
			</v-chip>
		</div>
		<div v-else>
			<v-progress-linear indeterminate color="primary" size="4" />
		</div>
		<v-pagination
			v-model="currentPage"
			density="compact"
			:length="noOfPages"
			class="my-5"
		></v-pagination>
	</v-card>
</template>

<style scoped lang="scss">
.bold {
	font-weight: bold;
}
</style>
